/**
 * Stacked raw fuel / odometer charts. Both charts use the same numeric
 * timestamp x-scale and explicit min/max values so matching timestamps
 * render at the same horizontal position.
 */

import { Line } from "react-chartjs-2";
import type { TooltipItem } from "chart.js";

import { ensureChartDefaults } from "./chartDefaults";
import { usePalette } from "./usePalette";
import type {
  RawFuelLogPoint,
  RawOdometerLogPoint,
} from "../../../api/types";

export interface RawDataChartsProps {
  fuelPoints?: RawFuelLogPoint[];
  odometerPoints?: RawOdometerLogPoint[];
  start: string;
  end: string;
}

type RawPoint = { x: number; y: number };
const Y_AXIS_WIDTH = 72;

function timestamp(value: string): number {
  return new Date(value).getTime();
}

function axisFormatter(startMs: number, endMs: number) {
  const spanMs = Math.abs(endMs - startMs);
  const dayMs = 24 * 60 * 60 * 1000;
  const options: Intl.DateTimeFormatOptions =
    spanMs <= 2 * dayMs
      ? { hour: "2-digit", minute: "2-digit" }
      : spanMs <= 120 * dayMs
        ? { month: "short", day: "numeric" }
        : { month: "short", year: "2-digit" };
  const formatter = new Intl.DateTimeFormat(undefined, options);

  return (value: string | number) => {
    const date = new Date(Number(value));
    return Number.isNaN(date.getTime()) ? "" : formatter.format(date);
  };
}

function formatTooltipTitle(value: number): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function rawLineOptions(
  startMs: number,
  endMs: number,
  unit: string,
  decimals: number,
  beginAtZero = true,
) {
  const formatAxis = axisFormatter(startMs, endMs);

  return {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: { right: 4 },
    },
    scales: {
      x: {
        type: "linear",
        min: startMs,
        max: endMs,
        title: { display: true, text: "Time" },
        ticks: {
          callback: (value: string | number) => formatAxis(value),
          maxRotation: 0,
        },
      },
      y: {
        beginAtZero,
        afterFit: (scale: { width: number }) => {
          scale.width = Y_AXIS_WIDTH;
        },
        ticks: {
          callback: (value: string | number) =>
            `${Number(value).toFixed(decimals)} ${unit}`,
        },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items: TooltipItem<"line">[]) => {
            const x = items[0]?.parsed.x;
            return typeof x === "number" ? formatTooltipTitle(x) : "";
          },
          label: (ctx: TooltipItem<"line">) => {
            const y = ctx.parsed.y;
            return `${typeof y === "number" ? y.toFixed(decimals) : "0"} ${unit}`;
          },
        },
      },
    },
  } as const;
}

export default function RawDataCharts({
  fuelPoints = [],
  odometerPoints = [],
  start,
  end,
}: RawDataChartsProps) {
  const palette = usePalette();
  ensureChartDefaults(palette);

  const startMs = timestamp(start);
  const endMs = timestamp(end);
  const fuelData: RawPoint[] = fuelPoints.map((point) => ({
    x: timestamp(point.datetime),
    y: point.liters,
  }));
  const odometerData: RawPoint[] = odometerPoints.map((point) => ({
    x: timestamp(point.datetime),
    y: point.odometer_km,
  }));

  return (
    <div className="raw-data-charts">
      <article className="chart-card raw-data-chart-card">
        <h3 className="chart-card__title">Fuel Log ({fuelPoints.length})</h3>
        <div className="chart-card__canvas raw-data-chart-card__canvas">
          <Line
            data={{
              datasets: [
                {
                  label: "Liters",
                  data: fuelData,
                  borderColor: palette.accent,
                  backgroundColor: palette.accent + "22",
                  pointBackgroundColor: palette.accent,
                  pointBorderColor: palette.accent,
                  pointRadius: 4,
                  tension: 0.15,
                  fill: false,
                },
              ],
            }}
            options={rawLineOptions(startMs, endMs, "L", 1)}
          />
        </div>
      </article>

      <article className="chart-card raw-data-chart-card">
        <h3 className="chart-card__title">Odometer Log ({odometerPoints.length})</h3>
        <div className="chart-card__canvas raw-data-chart-card__canvas">
          <Line
            data={{
              datasets: [
                {
                  label: "Odometer",
                  data: odometerData,
                  borderColor: palette.success,
                  backgroundColor: palette.success + "22",
                  pointBackgroundColor: palette.success,
                  pointBorderColor: palette.success,
                  pointRadius: 4,
                  tension: 0.15,
                  fill: false,
                },
              ],
            }}
            options={rawLineOptions(startMs, endMs, "km", 0, false)}
          />
        </div>
      </article>
    </div>
  );
}
